import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import ICONS from "../../../../assets/Icons";
import CommonToast from "../../../../components/_utils/CommonToast";
import { env } from "../../../../env";
import axiosInstance from "../../../globalInspector/axiosInstance";

const baseUrl = env.REACT_APP_BASE_URL;

const getHeaders = () => ({
  "Content-Type": "application/json",
  "X-Api-Key": sessionStorage.getItem("q2p_token"),
});

export const scheduledBeneficiaryCreate = createAsyncThunk(
  "scheduleBeneficiaryTransactions/scheduledBeneficiaryCreate",
  async (values, { rejectWithValue }) => {
    const { scheduledBeneficiaryTxnData } = values;
    try {
      const response = await axiosInstance.post(
        `${baseUrl}/transactions/scheduled_beneficiary`,
        scheduledBeneficiaryTxnData,
        {
          headers: getHeaders(),
        },
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const scheduledBeneficiaryViewAll = createAsyncThunk(
  "scheduleBeneficiaryTransactions/scheduledBeneficiaryViewAll",
  async (values, { rejectWithValue }) => {
    const {
      page_no,
      page_item_count,
      amount,
      lifecycle_status,
      frequency,
      occurrences,
      slot,
      transaction_day,
      transaction_month,
      transaction_start_date,
      transaction_end_date,
      beneficiary_transaction_ids,
      payment_method,
      beneficiary_account_id,
      owner_account_id,
      confidential_transaction,
      start_date,
      end_date,
      category_id,
      search_string,
      distribution_lifecycle_status,
      scheduled_name,
      labels,
    } = values;
    try {
      const response = await axiosInstance.get(
        `${baseUrl}/transactions/scheduled_beneficiary`,
        {
          headers: getHeaders(),
          params: {
            page_no,
            page_size: page_item_count,
            amount,
            lifecycle_status,
            frequency,
            occurrences,
            slot,
            transaction_day,
            transaction_month,
            transaction_start_date,
            transaction_end_date,
            beneficiary_transaction_ids,
            payment_method,
            scheduled_name,
            beneficiary_account_id,
            owner_account_id,
            confidential_transaction:
              confidential_transaction === "Show only confidential"
                ? "Y"
                : confidential_transaction === "Hide confidential"
                  ? "N"
                  : undefined,
            start_date,
            end_date,
            search_string,
            category_id,
            distribution_lifecycle_status,
            labels,
          },
        },
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error?.message);
    }
  },
);

export const scheduledBeneficiaryDetailsById = createAsyncThunk(
  "scheduleBeneficiaryTransactions/scheduledBeneficiaryDetailsById",
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `${baseUrl}/transactions/scheduled_beneficiary/${id}`,
        {
          headers: getHeaders(),
        },
      );

      return response.data;
    } catch (error) {
      return rejectWithValue({
        status: error?.response?.status,
        data: error?.response?.data,
        message: error?.message,
      });
    }
  },
);

export const scheduledBeneficiaryApprove = createAsyncThunk(
  "scheduleBeneficiaryTransactions/scheduledBeneficiaryApprove",
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(
        `${baseUrl}/transactions/scheduled_beneficiary/approve`,
        {
          txn_id: Array.isArray(id) ? id : [id],
        },
        {
          headers: getHeaders(),
        },
      );

      if (!response.data?.status) {
        return rejectWithValue(response.data);
      }

      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error?.message);
    }
  },
);

export const scheduledBeneficiaryReject = createAsyncThunk(
  "scheduleBeneficiaryTransactions/scheduledBeneficiaryReject",
  async ({ id, reason }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(
        `${baseUrl}/transactions/scheduled_beneficiary/reject`,
        {
          txn_id: Array.isArray(id) ? id : [id],
          reason,
        },
        {
          headers: getHeaders(),
        },
      );

      return response.data?.status
        ? response.data
        : rejectWithValue(response.data);
    } catch (error) {
      return rejectWithValue(error?.response?.data || error?.message);
    }
  },
);

export const scheduledBeneficiaryDiscard = createAsyncThunk(
  "scheduleBeneficiaryTransactions/scheduledBeneficiaryDiscard",
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.delete(
        `${baseUrl}/transactions/scheduled_beneficiary/delete`,
        {
          headers: getHeaders(),
          data: {
            txn_id: [id],
          },
        },
      );
      if (!response.data?.status) {
        return rejectWithValue(response.data);
      }

      return response.data;
    } catch (error) {
      const discardError = error?.response?.data?.errors?.base?.[0]?.errors;
      const message = discardError?.join(", ") || error?.message;

      CommonToast({
        text: message,
        icon: ICONS.ERROR_WITH_WHITE_CIRCLE,
        variant: "var(--error)",
        buttonNeeded: false,
      });
      return rejectWithValue(error?.response?.data || error?.message);
    }
  },
);
export const scheduledBeneficiaryOccurrences = createAsyncThunk(
  "scheduleBeneficiaryTransactions/scheduledBeneficiaryOccurrences",
  async ({ paramsData }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `${baseUrl}/transactions/scheduled_beneficiary/calculate_occurrences`,
        {
          headers: getHeaders(),
          params: paramsData,
        },
      );

      return response.data;
    } catch (error) {
      if (error?.response) {
        const responseData = error.response.data;
        const errorMessage =
          responseData?.errors?.category?.[0] ||
          responseData?.errors?.additional_data?.[0] ||
          responseData?.errors?.base?.[0] ||
          responseData?.errors;

        CommonToast({
          text: errorMessage,
          icon: ICONS.ERROR_WITH_WHITE_CIRCLE,
          variant: "var(--error)",
          buttonNeeded: false,
        });
        return rejectWithValue(responseData);
      }

      return rejectWithValue(error);
    }
  },
);

export const scheduledBeneficiaryAddLabel = createAsyncThunk(
  "scheduleBeneficiaryTransactions/scheduledBeneficiaryAddLabel",
  async ({ record_ids, labels }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(
        `${baseUrl}/transactions/scheduled_beneficiary/add_label`,
        {
          record_ids,
          labels,
        },
        {
          headers: getHeaders(),
        },
      );

      return response.data?.status
        ? response.data
        : rejectWithValue(response.data);
    } catch (error) {
      return rejectWithValue(error?.response?.data || error?.message);
    }
  },
);

export const scheduledBeneficiaryRemoveLabel = createAsyncThunk(
  "scheduleBeneficiaryTransactions/scheduledBeneficiaryRemoveLabel",
  async ({ record_ids, labels }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(
        `${baseUrl}/transactions/scheduled_beneficiary/remove_label`,
        {
          record_ids,
          labels,
        },
        {
          headers: getHeaders(),
        },
      );

      return response.data?.status
        ? response.data
        : rejectWithValue(response.data);
    } catch (error) {
      return rejectWithValue(error?.response?.data || error?.message);
    }
  },
);

const scheduledBeneficiaryTransactionsSlice = createSlice({
  name: "scheduleBeneficiaryTransactions",
  initialState: {
    scheduledBeneficiaryCreateData: null,
    scheduledBeneficiaryCreateLoading: null,
    scheduledBeneficiaryCreateError: null,

    scheduledBeneficiaryViewData: null,
    scheduledBeneficiaryViewLoading: null,
    scheduledBeneficiaryViewError: null,

    scheduledBeneficiaryDetailsData: null,
    scheduledBeneficiaryDetailsLoading: null,
    scheduledBeneficiaryDetailsError: null,

    scheduledBeneficiaryApproveData: null,
    scheduledBeneficiaryApproveLoading: null,
    scheduledBeneficiaryApproveError: null,

    scheduledBeneficiaryRejectData: null,
    scheduledBeneficiaryRejectLoading: null,
    scheduledBeneficiaryRejectError: null,

    scheduledBeneficiaryDiscardData: null,
    scheduledBeneficiaryDiscardLoading: null,
    scheduledBeneficiaryDiscardError: null,

    scheduledBeneficiaryOccurrencesData: null,
    scheduledBeneficiaryOccurrencesLoading: null,
    scheduledBeneficiaryOccurrencesError: null,

    scheduledBeneficiaryAddLabelData: null,
    scheduledBeneficiaryAddLabelLoading: false,
    scheduledBeneficiaryAddLabelError: null,

    scheduledBeneficiaryRemoveLabelData: null,
    scheduledBeneficiaryRemoveLabelLoading: false,
    scheduledBeneficiaryRemoveLabelError: null,

    scheduledBeneficiaryAvailableLabels: [],

    storedPage: 1,
  },
  reducers: {
    resetStoredPage: (state) => {
      state.storedPage = 1;
    },
    setStoredPage: (state, action) => {
      state.storedPage = action.payload;
    },

    clearScheduledBeneficiaryErrors: (state) => {
      state.scheduledBeneficiaryApproveError = null;
      state.scheduledBeneficiaryRejectError = null;
    },

    setScheduledBeneficiaryDetails: (state, action) => {
      state.scheduledBeneficiaryDetailsData = action.payload;
    },
    resetBeneScheduleTransactionsReject: (state) => {
      state.scheduledBeneficiaryRejectData = false;
      state.scheduledBeneficiaryRejectLoading = false;
      state.scheduledBeneficiaryRejectError = null;
    },
    resetBeneScheduleTransactionsApproval: (state) => {
      state.scheduledBeneficiaryApproveData = false;
      state.scheduledBeneficiaryApproveLoading = false;
      state.scheduledBeneficiaryApproveError = null;
    },
    resetBeneTransactionsDetails: (state) => {
      state.scheduledBeneficiaryDetailsData = false;
      state.scheduledBeneficiaryDetailsLoading = false;
      state.scheduledBeneficiaryDetailsError = null;
    },
    resetScheduledBeneficiaryAddLabel: (state) => {
      state.scheduledBeneficiaryAddLabelData = null;
      state.scheduledBeneficiaryAddLabelLoading = false;
      state.scheduledBeneficiaryAddLabelError = null;
    },
    resetScheduledBeneficiaryRemoveLabel: (state) => {
      state.scheduledBeneficiaryRemoveLabelData = null;
      state.scheduledBeneficiaryRemoveLabelLoading = false;
      state.scheduledBeneficiaryRemoveLabelError = null;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(scheduledBeneficiaryCreate.pending, (state) => {
        state.scheduledBeneficiaryCreateLoading = true;
      })
      .addCase(scheduledBeneficiaryCreate.fulfilled, (state, action) => {
        state.scheduledBeneficiaryCreateLoading = false;
        state.scheduledBeneficiaryCreateData = action?.payload;
      })
      .addCase(scheduledBeneficiaryCreate.rejected, (state, action) => {
        state.scheduledBeneficiaryCreateLoading = false;
        state.scheduledBeneficiaryCreateData = false;
        state.scheduledBeneficiaryCreateError = action?.payload;
      })

      .addCase(scheduledBeneficiaryViewAll.pending, (state) => {
        state.scheduledBeneficiaryViewLoading = true;
      })
      .addCase(scheduledBeneficiaryViewAll.fulfilled, (state, action) => {
        state.scheduledBeneficiaryViewLoading = false;
        const isPendingRequest =
          action?.meta?.arg?.lifecycle_status === "pending";
        // if (!isPendingRequest) {
          state.scheduledBeneficiaryViewData = action?.payload;
          const availableLabels =
            action?.payload?.summary?.available_labels || [];
          state.scheduledBeneficiaryAvailableLabels = availableLabels;
          sessionStorage.setItem(
            "scheduleBeneficiaryTxnLabels",
            JSON.stringify(availableLabels),
          );
        // }
      })
      .addCase(scheduledBeneficiaryViewAll.rejected, (state, action) => {
        state.scheduledBeneficiaryViewLoading = false;
        state.scheduledBeneficiaryViewData = false;
        state.scheduledBeneficiaryViewError = action?.payload;
      })
      .addCase(scheduledBeneficiaryDetailsById.pending, (state) => {
        state.scheduledBeneficiaryDetailsLoading = true;
      })
      .addCase(scheduledBeneficiaryDetailsById.fulfilled, (state, action) => {
        state.scheduledBeneficiaryDetailsLoading = false;
        state.scheduledBeneficiaryDetailsData = action?.payload;
      })
      .addCase(scheduledBeneficiaryDetailsById.rejected, (state, action) => {
        state.scheduledBeneficiaryDetailsData = false;
        state.scheduledBeneficiaryDetailsLoading = false;
        state.scheduledBeneficiaryDetailsError = action?.payload;
      })

      .addCase(scheduledBeneficiaryApprove.pending, (state) => {
        state.scheduledBeneficiaryApproveLoading = true;
      })
      .addCase(scheduledBeneficiaryApprove.fulfilled, (state, action) => {
        state.scheduledBeneficiaryApproveLoading = false;
        state.scheduledBeneficiaryApproveData = action?.payload;
      })
      .addCase(scheduledBeneficiaryApprove.rejected, (state, action) => {
        sessionStorage.setItem(
          "ScheduleBeneficiaryMultiDataError",
          JSON.stringify(action.payload?.errors?.base),
        );
        state.scheduledBeneficiaryApproveData = false;
        state.scheduledBeneficiaryApproveLoading = false;
        state.scheduledBeneficiaryApproveError = action?.payload;
      })
      .addCase(scheduledBeneficiaryReject.pending, (state) => {
        state.scheduledBeneficiaryRejectLoading = true;
      })
      .addCase(scheduledBeneficiaryReject.fulfilled, (state, action) => {
        state.scheduledBeneficiaryRejectLoading = false;
        state.scheduledBeneficiaryRejectData = action?.payload;
      })
      .addCase(scheduledBeneficiaryReject.rejected, (state, action) => {
        sessionStorage.setItem(
          "ScheduleBeneficiaryMultiDataError",
          JSON.stringify(action.payload?.errors?.base),
        );
        state.scheduledBeneficiaryRejectLoading = false;
        state.schedulesbeneficiaryTxnRejectError = action?.payload;
        state.scheduledBeneficiaryRejectData = false;
      })
      .addCase(scheduledBeneficiaryDiscard.pending, (state) => {
        state.scheduledBeneficiaryDiscardLoading = true;
      })
      .addCase(scheduledBeneficiaryDiscard.fulfilled, (state, action) => {
        state.scheduledBeneficiaryDiscardLoading = false;
        state.scheduledBeneficiaryDiscardData = action?.payload;
      })
      .addCase(scheduledBeneficiaryDiscard.rejected, (state, action) => {
        state.scheduledBeneficiaryDiscardLoading = false;
        state.scheduledBeneficiaryDiscardError = action?.payload;
        state.scheduledBeneficiaryDiscardData = false;
      })
      .addCase(scheduledBeneficiaryOccurrences.pending, (state) => {
        state.scheduledBeneficiaryOccurrencesLoading = true;
      })
      .addCase(scheduledBeneficiaryOccurrences.fulfilled, (state, action) => {
        state.scheduledBeneficiaryOccurrencesLoading = false;
        state.scheduledBeneficiaryOccurrencesData = action?.payload;
      })
      .addCase(scheduledBeneficiaryOccurrences.rejected, (state, action) => {
        state.scheduledBeneficiaryOccurrencesLoading = false;
        state.scheduledBeneficiaryOccurrencesData = false;
        state.scheduledBeneficiaryOccurrencesError = action?.payload;
      })

      .addCase(scheduledBeneficiaryAddLabel.pending, (state) => {
        state.scheduledBeneficiaryAddLabelLoading = true;
        state.scheduledBeneficiaryAddLabelError = null;
      })
      .addCase(scheduledBeneficiaryAddLabel.fulfilled, (state, action) => {
        state.scheduledBeneficiaryAddLabelLoading = false;
        state.scheduledBeneficiaryAddLabelData = action.payload;
      })
      .addCase(scheduledBeneficiaryAddLabel.rejected, (state, action) => {
        state.scheduledBeneficiaryAddLabelLoading = false;
        state.scheduledBeneficiaryAddLabelData = null;
        state.scheduledBeneficiaryAddLabelError = action.payload || true;
      })

      .addCase(scheduledBeneficiaryRemoveLabel.pending, (state) => {
        state.scheduledBeneficiaryRemoveLabelLoading = true;
        state.scheduledBeneficiaryRemoveLabelError = null;
      })
      .addCase(scheduledBeneficiaryRemoveLabel.fulfilled, (state, action) => {
        state.scheduledBeneficiaryRemoveLabelLoading = false;
        state.scheduledBeneficiaryRemoveLabelData = action.payload;
      })
      .addCase(scheduledBeneficiaryRemoveLabel.rejected, (state, action) => {
        state.scheduledBeneficiaryRemoveLabelLoading = false;
        state.scheduledBeneficiaryRemoveLabelData = null;
        state.scheduledBeneficiaryRemoveLabelError = action.payload || true;
      });
  },
});

export const {
  resetStoredPage,
  setStoredPage,
  clearScheduledBeneficiaryErrors,
  setScheduledBeneficiaryDetails,
  resetBeneTransactionsDetails,
  resetBeneScheduleTransactionsApproval,
  resetBeneScheduleTransactionsReject,
  resetScheduledBeneficiaryAddLabel,
  resetScheduledBeneficiaryRemoveLabel,
} = scheduledBeneficiaryTransactionsSlice.actions;
export default scheduledBeneficiaryTransactionsSlice.reducer;
